import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import { createChat } from "../../../actions/chatActions";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";
import {
  acceptDeal,
  cancelDeal,
  closeDeal,
  declineDeal,
  fetchDealById,
  isRequestAbortError,
  withdrawDeal,
} from "../api/deals";
import { fetchPropertySummary } from "../api/properties";
import { getDealPartnerChatTarget } from "../lib/dealPartnerChatTarget";
import { getDealStatusLabel } from "../lib/dealStatusLabels";
import {
  getDealLifecycleAction,
  getViewerRoleLabel,
} from "../lib/dealLifecycleActions";
import {
  formatDealEventForTimeline,
  sortDealEventsChronologically,
} from "../lib/dealEventTimeline";
import {
  getDealSidePresentation,
  hasPersonalDealParticipation,
} from "../lib/dealSidePresentation";
import DealStatusActions from "../ui/DealStatusActions";
import { DealSidePartyCard, PartyCard } from "../ui/DealSidePartyCard";
import DealPrivateFinanceSection from "./DealPrivateFinanceSection";
import DealPrivateTasksSection from "./DealPrivateTasksSection";
import DealPrivateViewingsSection from "./DealPrivateViewingsSection";
import DealRepresentativeReassign from "./DealRepresentativeReassign";
import { getPropertyStatusLabel } from "../../realty/lib/propertyLifecycle";
import {
  dealTypeLabel,
  formatDateTimeUk,
  formatDealCompensation,
  formatLocationSummary,
  formatMoney,
  getPropertyMainImageUrl,
  partyDisplayName,
  propertyTitleFromDeal,
  propertyTypeLabel,
} from "../lib/dealFormatting";

export { PartyCard };

const PARTNER_CHAT_ERROR =
  "Не вдалося відкрити чат з партнером. Спробуйте пізніше.";

const isValidDealId = (raw) => {
  if (raw == null || raw === "") return false;
  const parsed = Number.parseInt(String(raw), 10);
  return Number.isFinite(parsed) && parsed > 0 && String(parsed) === String(raw).trim();
};

function DealDetailScreenContent() {
  const { dealId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [deal, setDeal] = useState(null);
  const [propertyState, setPropertyState] = useState("idle");
  const [property, setProperty] = useState(null);
  const [pendingActionId, setPendingActionId] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusMessageTone, setStatusMessageTone] = useState("success");
  const [transitionError, setTransitionError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState(null);
  const [retryToken, setRetryToken] = useState(0);
  const [isCreatingPartnerChat, setIsCreatingPartnerChat] = useState(false);
  const [partnerChatError, setPartnerChatError] = useState(null);
  const mutationControllerRef = useRef(null);
  const mountedRef = useRef(true);
  const routeDealIdRef = useRef(dealId);
  const partnerChatRequestTokenRef = useRef(0);
  const partnerChatInFlightRef = useRef(null);
  const authoritativeRefetchControllerRef = useRef(null);
  const authoritativeRefetchTokenRef = useRef(0);

  // Track route deal id synchronously during render (not only in an effect).
  routeDealIdRef.current = dealId;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      partnerChatRequestTokenRef.current += 1;
      partnerChatInFlightRef.current = null;
      authoritativeRefetchTokenRef.current += 1;
      if (authoritativeRefetchControllerRef.current) {
        authoritativeRefetchControllerRef.current.abort();
        authoritativeRefetchControllerRef.current = null;
      }
    };
  }, []);

  const loadLiveProperty = (propertyId, signal, ownershipCheck) => {
    const mayContinue = () => {
      if (signal && signal.aborted) return false;
      if (typeof ownershipCheck === "function" && !ownershipCheck()) return false;
      return true;
    };
    if (!propertyId) {
      if (!mayContinue()) return Promise.resolve(null);
      setPropertyState("missing");
      setProperty(null);
      return Promise.resolve(null);
    }
    if (!mayContinue()) return Promise.resolve(null);
    setPropertyState("loading");
    return fetchPropertySummary(propertyId, { signal })
      .then((propertyData) => {
        if (!mayContinue()) return null;
        setProperty(propertyData);
        setPropertyState("loaded");
        return propertyData;
      })
      .catch((error) => {
        if (!mayContinue()) return null;
        if (isRequestAbortError(error)) return null;
        setPropertyState("error");
        setProperty(null);
        return null;
      });
  };

  useEffect(() => {
    partnerChatRequestTokenRef.current += 1;
    partnerChatInFlightRef.current = null;
    setIsCreatingPartnerChat(false);
    setPartnerChatError(null);

    // Supersede any in-flight authoritative Deal+Property refetch for the
    // previous route Deal before starting the primary load.
    authoritativeRefetchTokenRef.current += 1;
    if (authoritativeRefetchControllerRef.current) {
      authoritativeRefetchControllerRef.current.abort();
      authoritativeRefetchControllerRef.current = null;
    }

    if (!isValidDealId(dealId)) {
      setLoadState("not-found");
      return undefined;
    }

    const controller = new AbortController();
    const ownedRouteDealId = String(dealId).trim();
    const ownershipCheck = () =>
      mountedRef.current
      && !controller.signal.aborted
      && String(routeDealIdRef.current) === ownedRouteDealId;

    setLoadState("loading");
    setLoadError(null);
    setDeal(null);
    setProperty(null);
    setPropertyState("idle");
    setPendingActionId(null);
    setIsTransitioning(false);
    setStatusMessage(null);
    setTransitionError(null);
    setFieldErrors(null);

    fetchDealById(dealId, { signal: controller.signal })
      .then((data) => {
        if (!ownershipCheck()) return null;
        setDeal(data);
        setLoadState("loaded");
        return loadLiveProperty(data.property, controller.signal, ownershipCheck);
      })
      .catch((error) => {
        if (!ownershipCheck() || isRequestAbortError(error)) return;
        const status = error && error.response && error.response.status;
        if (status === 404) {
          setLoadState("not-found");
        } else {
          setLoadError(messageFromError(error));
          setLoadState("error");
        }
      });

    return () => {
      controller.abort();
      if (mutationControllerRef.current) {
        mutationControllerRef.current.abort();
        mutationControllerRef.current = null;
      }
      authoritativeRefetchTokenRef.current += 1;
      if (authoritativeRefetchControllerRef.current) {
        authoritativeRefetchControllerRef.current.abort();
        authoritativeRefetchControllerRef.current = null;
      }
    };
  }, [dealId, retryToken]);

  const handleWritePartner = async () => {
    const routeDealId = routeDealIdRef.current;
    if (
      !deal
      || !isValidDealId(routeDealId)
      || String(deal.id) !== String(routeDealId).trim()
    ) {
      return;
    }

    const target = getDealPartnerChatTarget(deal);
    if (!target) return;

    // Synchronous duplicate guard — claimed before React commits pending UI.
    if (partnerChatInFlightRef.current) return;

    const requestToken = ++partnerChatRequestTokenRef.current;
    const ownedDealId = String(deal.id);
    partnerChatInFlightRef.current = { token: requestToken, dealId: ownedDealId };

    setIsCreatingPartnerChat(true);
    setPartnerChatError(null);

    try {
      const createdChat = await dispatch(
        createChat([target.id], undefined, undefined, {
          publishTransientState: false,
        }),
      );

      const stillOwnsRequest =
        mountedRef.current
        && requestToken === partnerChatRequestTokenRef.current
        && partnerChatInFlightRef.current
        && partnerChatInFlightRef.current.token === requestToken
        && String(routeDealIdRef.current) === ownedDealId;

      if (!stillOwnsRequest) return;

      if (createdChat && createdChat.id != null && createdChat.id !== "") {
        navigate(`/chats/${createdChat.id}`);
        return;
      }

      setPartnerChatError(PARTNER_CHAT_ERROR);
    } finally {
      // Only the request instance that owns the guard may release it.
      if (
        partnerChatInFlightRef.current
        && partnerChatInFlightRef.current.token === requestToken
      ) {
        partnerChatInFlightRef.current = null;
        if (
          mountedRef.current
          && requestToken === partnerChatRequestTokenRef.current
          && String(routeDealIdRef.current) === ownedDealId
        ) {
          setIsCreatingPartnerChat(false);
        }
      }
    }
  };

  const handleSelectAction = (actionId) => {
    setTransitionError(null);
    setFieldErrors(null);
    setStatusMessage(null);
    setPendingActionId(actionId);
  };

  const handleCancelConfirmation = () => {
    if (isTransitioning) return;
    setPendingActionId(null);
    setTransitionError(null);
    setFieldErrors(null);
  };

  const runTypedAction = (actionId, expectedStatus, reason, signal) => {
    if (actionId === "accept") {
      return acceptDeal(deal.id, expectedStatus, { signal });
    }
    if (actionId === "decline") {
      return declineDeal(deal.id, expectedStatus, { reason, signal });
    }
    if (actionId === "withdraw") {
      return withdrawDeal(deal.id, expectedStatus, { reason, signal });
    }
    if (actionId === "cancel") {
      return cancelDeal(deal.id, expectedStatus, reason, { signal });
    }
    if (actionId === "close") {
      return closeDeal(deal.id, expectedStatus, { signal });
    }
    return Promise.reject(new Error(`Unknown action: ${actionId}`));
  };

  const handleConfirmAction = async (actionId, reason) => {
    if (!deal || isTransitioning) return;
    const action = getDealLifecycleAction(actionId);
    if (!action) return;

    if (mutationControllerRef.current) {
      mutationControllerRef.current.abort();
    }
    const controller = new AbortController();
    mutationControllerRef.current = controller;

    setIsTransitioning(true);
    setTransitionError(null);
    setFieldErrors(null);
    setStatusMessage(null);

    try {
      const updatedDeal = await runTypedAction(
        actionId,
        deal.status,
        reason,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setDeal(updatedDeal);
      setPendingActionId(null);
      setStatusMessageTone("success");
      setStatusMessage("Статус угоди оновлено");
      await loadLiveProperty(updatedDeal.property, controller.signal);
    } catch (error) {
      if (controller.signal.aborted || isRequestAbortError(error)) {
        return;
      }
      const httpStatus = error && error.response && error.response.status;
      if (httpStatus === 409) {
        setPendingActionId(null);
        setStatusMessageTone("success");
        setStatusMessage("Статус угоди вже змінився. Дані оновлено.");
        try {
          const fresh = await fetchDealById(deal.id, {
            signal: controller.signal,
          });
          if (!controller.signal.aborted) {
            setDeal(fresh);
            await loadLiveProperty(fresh.property, controller.signal);
          }
        } catch (refetchError) {
          if (!isRequestAbortError(refetchError)) {
            setTransitionError(messageFromError(refetchError));
          }
        }
      } else if (httpStatus === 404) {
        setLoadState("not-found");
      } else if (httpStatus === 400) {
        const fields = parseDrfFieldErrors(error, [
          "reason",
          "expected_status",
          "detail",
        ]);
        setFieldErrors(fields);
        setTransitionError(fields._form || messageFromError(error));
      } else {
        setTransitionError(messageFromError(error));
      }
    } finally {
      if (mutationControllerRef.current === controller) {
        mutationControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setIsTransitioning(false);
      }
    }
  };

  const refetchDealAuthoritative = async () => {
    if (!isValidDealId(dealId)) return;

    const ownedRouteDealId = String(dealId).trim();
    if (authoritativeRefetchControllerRef.current) {
      authoritativeRefetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    authoritativeRefetchControllerRef.current = controller;
    const requestToken = ++authoritativeRefetchTokenRef.current;

    const stillOwns = () =>
      mountedRef.current
      && requestToken === authoritativeRefetchTokenRef.current
      && !controller.signal.aborted
      && String(routeDealIdRef.current) === ownedRouteDealId;

    try {
      const fresh = await fetchDealById(ownedRouteDealId, {
        signal: controller.signal,
      });
      if (!stillOwns()) return;
      setDeal(fresh);
      await loadLiveProperty(fresh.property, controller.signal, stillOwns);
    } catch (error) {
      if (!stillOwns() || isRequestAbortError(error)) return;
      const status = error && error.response && error.response.status;
      if (status === 404) {
        setLoadState("not-found");
      }
    } finally {
      if (
        authoritativeRefetchControllerRef.current === controller
        && requestToken === authoritativeRefetchTokenRef.current
      ) {
        authoritativeRefetchControllerRef.current = null;
      }
    }
  };

  const handleReassignDealUpdated = (updatedDeal, meta) => {
    if (meta && meta.conflict) {
      refetchDealAuthoritative();
      return;
    }
    if (
      updatedDeal
      && String(routeDealIdRef.current) === String(dealId).trim()
      && String(updatedDeal.id) === String(dealId).trim()
    ) {
      setDeal(updatedDeal);
      setStatusMessageTone("success");
      setStatusMessage("Представника оновлено");
    }
  };

  if (loadState === "loading") {
    return (
      <div className="feature-deal-detail__state">
        <Spinner size="lg" label="Завантаження угоди" />
      </div>
    );
  }

  if (loadState === "not-found") {
    return (
      <div className="feature-deal-detail__state">
        <EmptyState
          icon="fa-solid fa-circle-question"
          title="Угоду не знайдено або у вас немає до неї доступу"
          size="md"
          primaryAction={
            <Link to="/deals" className="action-btn">
              Повернутися до списку угод
            </Link>
          }
        />
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="feature-deal-detail__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити угоду"
          description={loadError || "Спробуйте оновити сторінку."}
          size="md"
          primaryAction={
            <button
              type="button"
              className="action-btn"
              onClick={() => setRetryToken((value) => value + 1)}
            >
              Спробувати знову
            </button>
          }
        />
      </div>
    );
  }

  const propertyImageUrl = getPropertyMainImageUrl(property);
  const clientName =
    deal.client_summary && deal.client_summary.name
      ? deal.client_summary.name
      : null;
  const timeline = sortDealEventsChronologically(deal.events).map(
    formatDealEventForTimeline,
  );
  const headingTitle = propertyTitleFromDeal(deal, property);
  // Fail closed: never expose CTA when rendered Deal payload ≠ current route id.
  const routeMatchesDeal =
    deal != null
    && isValidDealId(dealId)
    && String(deal.id) === String(dealId).trim();
  const partnerChatTarget = routeMatchesDeal
    ? getDealPartnerChatTarget(deal)
    : null;
  const sellerSide = getDealSidePresentation(deal, "seller");
  const buyerSide = getDealSidePresentation(deal, "buyer");
  const mountPrivatePersonalSurfaces = hasPersonalDealParticipation(deal);
  // Client CRM link only when server projects an authorized client_id.
  const clientCrmId =
    deal.client_id != null && deal.client_id !== ""
      ? deal.client_id
      : null;

  return (
    <div className="feature-deal-detail" data-feature="deal-detail">
      <header className="clients-header feature-deal-detail__header">
        <div className="clients-header__title">
          <h1 className="clients-header__heading">
            Угода #{deal.id}
          </h1>
          <p className="feature-deal-detail__subtitle">{headingTitle}</p>
          <div className="feature-deal-detail__header-meta">
            <span
              className={[
                "feature-deals-list__status-badge",
                `feature-deals-list__status-badge--${deal.status}`,
              ].join(" ")}
            >
              {getDealStatusLabel(deal.status)}
            </span>
            <span className="feature-deal-detail__role-pill">
              {getViewerRoleLabel(deal.viewer_role)}
            </span>
            {deal.is_internal ? (
              <span className="feature-deal-detail__role-pill">Внутрішня</span>
            ) : null}
          </div>
        </div>
        <div className="clients-header__actions feature-deal-detail__header-actions">
          {partnerChatTarget ? (
            <button
              type="button"
              className="btn-more"
              onClick={handleWritePartner}
              disabled={isCreatingPartnerChat}
            >
              <i className="fa-solid fa-comments" aria-hidden="true" />
              {isCreatingPartnerChat ? "Відкриття чату…" : "Написати партнеру"}
            </button>
          ) : null}
          <Link to="/deals" className="action-btn">
            <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            {" "}
            До списку угод
          </Link>
        </div>
      </header>

      {partnerChatError ? (
        <p className="feature-deal-detail__status-error" role="alert">
          {partnerChatError}
        </p>
      ) : null}

      <DealStatusActions
        deal={deal}
        pendingActionId={pendingActionId}
        isTransitioning={isTransitioning}
        statusMessage={statusMessage}
        statusMessageTone={statusMessageTone}
        errorMessage={transitionError}
        fieldErrors={fieldErrors}
        onSelectAction={handleSelectAction}
        onCancelConfirmation={handleCancelConfirmation}
        onConfirmAction={handleConfirmAction}
        onReasonDraftChange={() => {
          setFieldErrors((prev) => {
            if (!prev || !prev.reason) return prev;
            const next = { ...prev };
            delete next.reason;
            return Object.keys(next).length ? next : null;
          });
        }}
      />

      <section
        className="feature-deal-detail__section"
        aria-labelledby="deal-financial-heading"
      >
        <h2 id="deal-financial-heading">Фінанси угоди</h2>
        <dl className="feature-deal-detail__facts">
          <div>
            <dt>Ціна угоди</dt>
            <dd className="feature-deals-list__cell-numeric">
              {formatMoney(deal.price, deal.currency)}
            </dd>
          </div>
          {!deal.is_internal ? (
            <div>
              <dt>Винагорода партнеру</dt>
              <dd className="feature-deals-list__cell-numeric">
                {formatDealCompensation(deal)}
              </dd>
            </div>
          ) : null}
          <div>
            <dt>Створено</dt>
            <dd className="feature-deals-list__cell-numeric">
              {formatDateTimeUk(deal.created_at)}
            </dd>
          </div>
          {deal.updated_at ? (
            <div>
              <dt>Оновлено</dt>
              <dd className="feature-deals-list__cell-numeric">
                {formatDateTimeUk(deal.updated_at)}
              </dd>
            </div>
          ) : null}
        </dl>
        {deal.proposal_note ? (
          <p className="feature-deal-detail__note">
            <strong>Примітка пропозиції:</strong> {deal.proposal_note}
          </p>
        ) : null}
        {clientName ? (
          <p className="feature-deal-detail__note">
            <strong>Клієнт:</strong> {clientName}
            {clientCrmId ? (
              <>
                {" "}
                <Link to={`/clients/${clientCrmId}`}>Відкрити в CRM</Link>
              </>
            ) : null}
          </p>
        ) : null}
      </section>

      <section
        className="feature-deal-detail__section"
        aria-labelledby="deal-parties-heading"
      >
        <h2 id="deal-parties-heading">Сторони</h2>
        <div className="feature-deal-detail__parties">
          <DealSidePartyCard presentation={sellerSide}>
            <DealRepresentativeReassign
              deal={deal}
              presentation={sellerSide}
              routeDealId={dealId}
              onDealUpdated={handleReassignDealUpdated}
              onDealInaccessible={() => setLoadState("not-found")}
            />
          </DealSidePartyCard>
          <DealSidePartyCard presentation={buyerSide}>
            <DealRepresentativeReassign
              deal={deal}
              presentation={buyerSide}
              routeDealId={dealId}
              onDealUpdated={handleReassignDealUpdated}
              onDealInaccessible={() => setLoadState("not-found")}
            />
          </DealSidePartyCard>
        </div>
      </section>

      <section
        className="feature-deal-detail__section"
        aria-labelledby="deal-property-heading"
      >
        <h2 id="deal-property-heading">Об&apos;єкт</h2>
        {propertyState === "loading" ? (
          <Spinner size="md" label="Завантаження об'єкта" />
        ) : null}

        {propertyState === "error" || propertyState === "missing" ? (
          <div className="feature-deal-detail__property-fallback">
            <p>{propertyTitleFromDeal(deal, null)}</p>
            {deal.property_deal_type_snapshot ? (
              <p>{dealTypeLabel(deal.property_deal_type_snapshot)}</p>
            ) : null}
            <p>Показано знімок угоди — живий об&apos;єкт недоступний.</p>
          </div>
        ) : null}

        {propertyState === "loaded" && property ? (
          <div className="feature-deal-detail__property">
            {propertyImageUrl ? (
              <img
                src={propertyImageUrl}
                alt={propertyTitleFromDeal(deal, property)}
                className="feature-deal-detail__property-image"
              />
            ) : null}
            <div>
              <h3>{propertyTitleFromDeal(deal, property)}</h3>
              <p>{getPropertyStatusLabel(property.status)}</p>
              <p>
                {propertyTypeLabel(property.property_type)} ·{" "}
                {dealTypeLabel(property.deal_type)}
              </p>
              <p>{formatMoney(property.price, property.currency)}</p>
              <p>{formatLocationSummary(property.location)}</p>
              {property.created_by ? (
                <p>Власник: {partyDisplayName(property.created_by)}</p>
              ) : null}
              {deal.property ? (
                <Link to={`/realty/${deal.property}`} className="action-btn">
                  Переглянути об&apos;єкт
                </Link>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      {mountPrivatePersonalSurfaces ? (
        <DealPrivateTasksSection dealId={deal.id} />
      ) : null}

      {mountPrivatePersonalSurfaces ? (
        <DealPrivateFinanceSection
          dealId={deal.id}
          dealCurrency={deal.currency}
          dealPrice={deal.price}
        />
      ) : null}

      {mountPrivatePersonalSurfaces ? (
        <DealPrivateViewingsSection dealId={deal.id} />
      ) : null}

      <section
        className="feature-deal-detail__section"
        aria-labelledby="deal-timeline-heading"
      >
        <h2 id="deal-timeline-heading">Історія подій</h2>
        {timeline.length === 0 ? (
          <p className="feature-deal-detail__empty-compact">Подій поки немає.</p>
        ) : (
          <ol className="feature-deal-detail__timeline">
            {timeline.map((item, index) => (
              <li
                key={item.id != null ? item.id : `event-${index}`}
                className="feature-deal-detail__timeline-item"
              >
                <span
                  className="feature-deal-detail__timeline-marker"
                  aria-hidden="true"
                />
                <div className="feature-deal-detail__timeline-body">
                  <div className="feature-deal-detail__timeline-title">
                    {item.title}
                  </div>
                  <div className="feature-deal-detail__timeline-meta">
                    <time>{item.timeLabel}</time>
                    {item.actorLabel ? <span>· {item.actorLabel}</span> : null}
                  </div>
                  {item.transitionLabel ? (
                    <div className="feature-deal-detail__timeline-transition">
                      {item.transitionLabel}
                    </div>
                  ) : null}
                  {item.note ? (
                    <div className="feature-deal-detail__timeline-note">
                      Причина: {item.note}
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

export default DealDetailScreenContent;
